import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight, BookOpen, Instagram, Package, Search, Send, Youtube } from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice, localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { usePageTitle } from '@/lib/usePageTitle'
import { trackSearch } from '@/lib/analytics'
import { ImageCarousel, type CarouselSlide } from '@/components/ImageCarousel'
import { YouTubeCarousel } from '@/components/YouTubeCarousel'
import type { BannerSlideOut, Category, ProductSummary, StaticPage } from '@/shared/types'

interface PublicSettings {
  shop_name: string
  brand_logo_url: string | null
  youtube_channel_id?: string
}

const YOUTUBE_VIDEOS: Array<{ videoId: string; title_uk: string; title_en: string }> = [
  { videoId: 'q9hIg7r_sg0', title_uk: 'Регулювання точилки', title_en: 'Sharpener adjustment' },
  { videoId: 'AJQvji_9a1U', title_uk: 'Налаштування точилки', title_en: 'Sharpener setup' },
]

const YOUTUBE_CHANNEL_URL =
  'https://www.youtube.com/@%D0%AE%D1%80%D0%B0%D0%9A%D0%BB%D0%BE%D0%B4%D1%87%D0%B8%D0%BA'
const INSTAGRAM_URL = 'https://www.instagram.com/klodchik_/'

export function HomePage() {
  const lang = useAppStore((s) => s.lang)
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const { data: dbBanners } = useQuery<BannerSlideOut[]>({
    queryKey: ['banners'],
    queryFn: () => api.get<BannerSlideOut[]>('/banners'),
    staleTime: 300_000,
  })

  const heroSlides: CarouselSlide[] = useMemo(() => {
    if (dbBanners && dbBanners.length > 0) {
      return dbBanners.map((b) => ({
        imageUrl: b.image_url,
        alt: b.alt_text || 'Banner',
        href: b.href || '/catalog',
      }))
    }
    return []
  }, [dbBanners])

  const { data: products } = useQuery<ProductSummary[]>({
    queryKey: ['products', 'home-klodchik'],
    queryFn: () => api.get<ProductSummary[]>('/products'),
  })

  const { data: categories } = useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
    staleTime: 300_000,
  })

  const { data: pages } = useQuery<StaticPage[]>({
    queryKey: ['pages'],
    queryFn: () => api.get<StaticPage[]>('/pages'),
    staleTime: 300_000,
  })

  usePageTitle(lang === 'en' ? 'KLODCHIK — Sharpening jigs' : 'KLODCHIK — Точилки для ножів')

  const flagshipSharpener = useMemo(
    () =>
      (products ?? []).find(
        (p) =>
          p.slug === 'helicopter' ||
          p.slug === 'tochylka-hedikopter' ||
          p.name_uk.toLowerCase().includes('гелікоптер'),
      ) || (products ?? [])[0],
    [products],
  )

  const microscopeSharpener = useMemo(
    () =>
      (products ?? []).find(
        (p) => p.slug === 'mbi' || p.name_uk.toLowerCase().includes('мікроскоп'),
      ),
    [products],
  )
  const newest = (products ?? []).slice(0, 8)

  const { data: suggestions } = useQuery<{
    products: Array<{ id: number; name: string; price_uah: number; photo_url: string; category_name?: string; slug?: string }>
    brands: string[]
    categories: string[]
  }>({
    queryKey: ['search-suggestions', searchQuery],
    queryFn: () => api.get(`/products/search-suggestions?q=${encodeURIComponent(searchQuery.trim())}`),
    enabled: searchQuery.trim().length >= 2,
    staleTime: 30_000,
  })

  const searchResults = useMemo(() => {
    if (!suggestions?.products) return []
    return suggestions.products.map((p) => ({
      id: p.id,
      name_uk: p.name,
      name_en: p.name,
      price_uah: p.price_uah,
      price_usd: p.price_uah,
      photo_url: p.photo_url,
      category_name_uk: p.category_name,
      category_name_en: p.category_name,
      slug: p.slug || String(p.id),
      in_stock: true,
      stock_qty: null,
      is_made_to_order: false,
      production_days: 0,
      category_id: null,
      store_id: null,
      is_kit: false,
    }))
  }, [suggestions])

  useEffect(() => {
    setIsOpen(searchQuery.trim().length >= 2) // eslint-disable-line react-hooks/set-state-in-effect
  }, [searchQuery])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      trackSearch(searchQuery.trim())
      navigate({ to: '/catalog', search: { q: searchQuery.trim() } })
      setIsOpen(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#15181c] pb-24 text-[#e8e8e8]">
      <div className="mx-auto max-w-2xl">
      <div className="flex items-center justify-between px-5 pt-6 pb-4">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={settings?.brand_logo_url || '/app/assets/klodchik-logo.jpg'}
            alt="KLODCHIK"
            className="h-11 w-11 rounded-full object-cover border border-[#2a2f36] drop-shadow-md transition group-hover:scale-105"
          />
          <div>
            <div className="text-base font-black tracking-wider text-white">KLODCHIK</div>
            <div className="text-[11px] font-medium text-[#c9a24b]">
              {lang === 'en' ? 'Sharpening Systems Workshop' : 'Майстерня точильних систем'}
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <a
            href={YOUTUBE_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#2a2f36] bg-[#1b1f24] text-red-500 hover:bg-[#252a30] transition"
          >
            <Youtube className="h-4 w-4" />
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#2a2f36] bg-[#1b1f24] text-pink-400 hover:bg-[#252a30] transition"
          >
            <Instagram className="h-4 w-4" />
          </a>
        </div>
      </div>

      <div className="relative mx-5 mb-4" ref={searchContainerRef}>
        <form onSubmit={handleSearchSubmit} className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (searchQuery.trim().length >= 2) setIsOpen(true)
            }}
            placeholder={lang === 'en' ? 'Search products...' : lang === 'ru' ? 'Поиск товаров...' : 'Пошук товарів...'}
            className="w-full rounded-2xl border border-[#2a2f36] bg-[#1b1f24] py-3 pl-11 pr-4 text-sm text-white placeholder-[#6a7078] focus:outline-none focus:ring-2 focus:ring-[#c9a24b]"
          />
          <Search className="absolute left-4 top-3.5 h-4 w-4 text-[#6a7078]" />
        </form>

        {isOpen && (
          <div className="absolute left-0 right-0 z-50 mt-1 max-h-80 overflow-y-auto rounded-2xl border border-[#2a2f36] bg-[#1b1f24] shadow-2xl">
            {searchResults.length > 0 ? (
              <div className="p-2 space-y-1">
                {searchResults.slice(0, 6).map((product) => (
                  <Link
                    key={product.id}
                    to="/catalog/$productSlug"
                    params={{ productSlug: product.slug || String(product.id) }}
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 rounded-xl p-2 transition hover:bg-[#252a30]"
                  >
                    <div className="h-10 w-10 shrink-0 rounded-lg bg-[#252a30] overflow-hidden flex items-center justify-center">
                      {product.photo_url ? (
                        <img src={product.photo_url} alt={product.name_uk} className="h-full w-full object-cover" />
                      ) : (
                        <Search className="h-4 w-4 text-[#6a7078]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-[#e8e8e8] truncate">
                        {lang === 'en' ? product.name_en || product.name_uk : product.name_uk}
                      </div>
                      <div className="text-xs text-[#c9a24b] font-semibold">{product.price_uah} ₴</div>
                    </div>
                  </Link>
                ))}
                <Link
                  to="/catalog"
                  search={{ q: searchQuery }}
                  onClick={() => setIsOpen(false)}
                  className="block w-full py-2.5 text-center text-xs font-semibold text-[#c9a24b] hover:bg-[#252a30] rounded-xl transition"
                >
                  {lang === 'en' ? 'Show all results' : lang === 'ru' ? 'Показать все результаты' : 'Показати всі результати'}
                </Link>
              </div>
            ) : (
              <div className="p-4 text-center text-xs text-[#8a9099]">
                {lang === 'en' ? 'No results found' : lang === 'ru' ? 'Ничего не найдено' : 'Нічого не знайдено'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CATEGORY CHIPS */}
      {categories && categories.length > 0 && (
        <div className="mx-5 mb-5 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
          <Link
            to="/catalog"
            className="shrink-0 rounded-xl border border-[#c9a24b] bg-[#c9a24b]/10 px-3.5 py-1.5 text-xs font-semibold text-[#c9a24b] transition hover:bg-[#c9a24b]/20"
          >
            {lang === 'en' ? 'All Products' : 'Всі товари'}
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to="/catalog"
              search={{ category: cat.id }}
              className="shrink-0 rounded-xl border border-[#2a2f36] bg-[#1b1f24] px-3.5 py-1.5 text-xs font-medium text-[#c0c5cc] transition hover:border-[#c9a24b]/50 hover:text-white"
            >
              {localizedName(lang, cat.name_uk, cat.name_en)}
            </Link>
          ))}
        </div>
      )}

      {/* HERO BANNER SLIDER (IF ACTIVE BANNERS EXIST IN DB) */}
      {heroSlides.length > 0 && (
        <div className="mx-5 mb-4 overflow-hidden rounded-3xl border border-[#2a2f36] shadow-2xl">
          <ImageCarousel slides={heroSlides} aspectClassName="aspect-[1640/624] h-auto w-full" />
        </div>
      )}

      {/* HERO HERO CARD (FEATURED PRODUCT IMAGE & TITLE) */}
      <div className="mx-5 overflow-hidden rounded-3xl border border-[#2a2f36] bg-gradient-to-br from-[#20242a] via-[#161a20] to-[#0f1114] shadow-2xl">
        {flagshipSharpener?.photo_url && (
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-[#0d0f12]">
            <img
              src={flagshipSharpener.photo_url}
              alt={localizedName(lang, flagshipSharpener.name_uk, flagshipSharpener.name_en)}
              className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#15181c] via-[#15181c]/30 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
              <span className="rounded-full bg-[#c9a24b]/20 px-3 py-1 text-xs font-bold text-[#c9a24b] border border-[#c9a24b]/40 backdrop-blur-md">
                ★ {localizedName(lang, flagshipSharpener.name_uk, flagshipSharpener.name_en)}
              </span>
              <span className="text-sm font-extrabold text-white drop-shadow-md">
                {formatPrice(flagshipSharpener.price_uah)}
              </span>
            </div>
          </div>
        )}
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="rounded-md bg-[#c9a24b]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#c9a24b] border border-[#c9a24b]/30">
              {lang === 'en' ? 'Flagship System' : 'Флагманська система'}
            </span>
            {flagshipSharpener?.is_made_to_order && (
              <span className="rounded-md bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-400 border border-amber-500/20">
                ⏳ {lang === 'en' ? 'Made to order (~4 weeks)' : 'Під замовлення (~4 тижні)'}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold leading-tight text-white">
            {lang === 'en' ? 'Every edge, the same angle. Every time.' : 'Один і той самий кут — щоразу.'}
          </h1>
          <p className="mt-2 text-sm text-[#aab0b8]">
            {lang === 'en'
              ? 'Sharpening jigs and accessories for a repeatable, precise result.'
              : 'Точилки та аксесуари для стабільного й точного результату заточки.'}
          </p>
          <div className="mt-4 flex items-center gap-3">
            {flagshipSharpener && (
              <Link
                to="/catalog/$productSlug"
                params={{ productSlug: flagshipSharpener.slug || String(flagshipSharpener.id) }}
                className="inline-flex items-center gap-1.5 rounded-2xl bg-[#c9a24b] px-4 py-2.5 text-sm font-semibold text-[#15181c] shadow-lg shadow-[#c9a24b]/10 transition hover:bg-[#d8b056]"
              >
                {lang === 'en' ? 'Flagship details' : 'Детальніше про флагман'}
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <Link
              to="/catalog"
              className="inline-flex items-center gap-1.5 rounded-2xl border border-[#2a2f36] bg-[#1b1f24] px-4 py-2.5 text-sm font-semibold text-[#e8e8e8] transition hover:bg-[#252a30]"
            >
              {lang === 'en' ? 'Catalog' : 'Каталог'}
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-5 mt-6 rounded-3xl border border-[#2a2f36] bg-[#1b1f24] p-5">
        <h2 className="text-base font-semibold text-white">
          {lang === 'en' ? 'About sharpening' : 'Про заточку'}
        </h2>
        <p className="mt-2 text-sm leading-6 text-[#aab0b8]">
          {lang === 'en'
            ? "Good sharpening isn't about force — it's about a precise, consistent angle. Even a small wandering angle turns a sharp edge dull after a few passes. That's why a tool that holds the angle steady from the first stroke to the last matters."
            : 'Правильна заточка — це не про силу, а про точний і стабільний кут. Навіть невеликий "гуляючий" кут перетворює гостре лезо на тупе за кілька проходів. Тому важливо мати інструмент, який тримає кут незмінним від першого до останнього руху.'}
        </p>
      </div>

      {microscopeSharpener && (
        <div className="mx-5 mt-4 overflow-hidden rounded-3xl border border-[#2a2f36] bg-[#1b1f24] p-5">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            {microscopeSharpener.photo_url && (
              <img
                src={microscopeSharpener.photo_url}
                alt={localizedName(lang, microscopeSharpener.name_uk, microscopeSharpener.name_en)}
                className="h-28 w-28 rounded-2xl object-cover border border-[#2a2f36] shrink-0"
              />
            )}
            <div className="flex-1">
              <h2 className="text-base font-semibold text-white">
                {localizedName(lang, microscopeSharpener.name_uk, microscopeSharpener.name_en)}
              </h2>
              <p className="mt-2 text-sm leading-6 text-[#aab0b8]">
                {lang === 'en'
                  ? "The microscope-based sharpening jig lets you see the edge under magnification and control every stroke. It's not about eyeballing it — it's about a repeatable, predictable result every time."
                  : 'Точилка на базі мікроскопа дозволяє бачити крайку леза під збільшенням і контролювати кожен рух заточки. Це не про "прикинути на око" — це про повторюваний, передбачуваний результат кожного разу.'}
              </p>
              <Link
                to="/catalog/$productSlug"
                params={{ productSlug: microscopeSharpener.slug || String(microscopeSharpener.id) }}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#c9a24b]"
              >
                {lang === 'en' ? 'View microscope sharpener' : 'Переглянути точилку на базі мікроскопа'}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}



      {/* MASTER CONSULTATION CTA */}
      <div className="mx-5 mt-6 rounded-3xl border border-[#c9a24b]/30 bg-gradient-to-br from-[#23211b] via-[#1a1c20] to-[#15181c] p-5 shadow-xl">
        <div className="flex items-start gap-3.5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#c9a24b]/20 text-[#c9a24b] border border-[#c9a24b]/40">
            <Send className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold text-white">
              {lang === 'en' ? 'Consultation with Master Yuri Klodchik' : 'Індивідуальна консультація майстра'}
            </h2>
            <p className="mt-1.5 text-xs leading-relaxed text-[#b0b6bf]">
              {lang === 'en'
                ? 'Need advice choosing a model, rotary clamp, or abrasive stones? Message Yuri directly in Telegram for guidance. Orders are placed directly on the website.'
                : 'Потрібна порада щодо вибору моделі, поворотного вузла чи сумісних абразивів? Напишіть майстру в Telegram для консультації. Замовлення оформлюється безпосередньо на сайті.'}
            </p>
            <a
              href="https://t.me/Gibeon4"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3.5 inline-flex items-center gap-2 rounded-xl bg-[#2563eb]/20 hover:bg-[#2563eb]/30 text-[#60a5fa] border border-[#3b82f6]/40 px-3.5 py-2 text-xs font-semibold transition"
            >
              <Send className="h-3.5 w-3.5" />
              {lang === 'en' ? 'Message Yuri in Telegram (@Gibeon4)' : 'Написати Юрію в Telegram (@Gibeon4)'}
            </a>
          </div>
        </div>
      </div>

      <div className="mx-5 mt-6">
        <h2 className="mb-3 text-base font-semibold text-white">
          {lang === 'en' ? 'Video guides' : 'Відео-інструкції'}
        </h2>
        {settings?.youtube_channel_id ? (
          <YouTubeCarousel channelId={settings.youtube_channel_id} />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {YOUTUBE_VIDEOS.map((video) => (
              <a
                key={video.videoId}
                href={`https://www.youtube.com/watch?v=${video.videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="overflow-hidden rounded-2xl border border-[#2a2f36] bg-[#1b1f24]"
              >
                <div className="h-28 w-full overflow-hidden bg-[#22262c]">
                  <img
                    src={`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
                <p className="px-2.5 py-2 text-xs font-medium text-[#d9dde1]">
                  {lang === 'en' ? video.title_en : video.title_uk}
                </p>
              </a>
            ))}
          </div>
        )}
        <a
          href={YOUTUBE_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-block text-xs font-semibold text-[#c9a24b]"
        >
          {lang === 'en' ? 'More videos on our channel →' : 'Більше відео на нашому каналі →'}
        </a>
      </div>

      <div className="mx-5 mt-6 flex items-center gap-4">
        <a
          href={YOUTUBE_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 rounded-full border border-[#2a2f36] px-3 py-1.5 text-xs font-medium text-[#d9dde1]"
        >
          <Youtube className="h-4 w-4" />
          YouTube
        </a>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 rounded-full border border-[#2a2f36] px-3 py-1.5 text-xs font-medium text-[#d9dde1]"
        >
          <Instagram className="h-4 w-4" />
          Instagram
        </a>
      </div>

      {newest.length > 0 && (
        <div className="mt-8 px-5">
          <h2 className="mb-3 text-base font-semibold text-white">
            {lang === 'en' ? 'Workshop Products' : 'Товари майстерні'}
          </h2>
          <ul className="grid grid-cols-2 gap-3">
            {newest.map((product) => {
              const name = localizedName(lang, product.name_uk, product.name_en)
              return (
                <li
                  key={product.id}
                  className="min-w-0 overflow-hidden rounded-2xl border border-[#2a2f36] bg-[#1b1f24] transition hover:border-[#c9a24b]/40"
                >
                  <Link to="/catalog/$productSlug" params={{ productSlug: product.slug || String(product.id) }}>
                    <div className="relative">
                      {product.photo_url ? (
                        <img src={product.photo_url} alt={name} className="h-32 w-full object-cover" />
                      ) : (
                        <div className="flex h-32 items-center justify-center bg-[#22262c]">
                          <Package className="h-8 w-8 text-[#4a5058]" />
                        </div>
                      )}
                      {product.is_made_to_order ? (
                        <span className="absolute bottom-2 left-2 rounded-md bg-amber-500/80 px-1.5 py-0.5 text-[10px] font-bold text-black backdrop-blur-sm">
                          ⏳ {lang === 'en' ? '~4 weeks' : '~4 тижні'}
                        </span>
                      ) : (
                        <span className="absolute bottom-2 left-2 rounded-md bg-emerald-500/80 px-1.5 py-0.5 text-[10px] font-bold text-white backdrop-blur-sm">
                          {lang === 'en' ? '✓ In stock' : '✓ В наявності'}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 p-2.5">
                      <p className="line-clamp-2 break-words text-sm font-medium leading-tight text-[#d9dde1]">
                        {name}
                      </p>
                      <p className="mt-1 text-sm font-bold text-[#c9a24b]">{formatPrice(product.price_uah)}</p>
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {pages && pages.length > 0 && (
        <div className="mx-5 mt-10 space-y-4 border-t border-[#2a2f36] pt-6">
          <h2 className="flex items-center gap-2 text-base font-bold text-white">
            <BookOpen className="h-4 w-4 text-[#c9a24b]" />
            {lang === 'en' ? 'Knowledge Base & Sharpening Guides' : 'База знань та корисні статті про заточку'}
          </h2>
          <div className="grid grid-cols-1 gap-3">
            {pages.map((page) => {
              const title = localizedName(lang, page.title_uk, page.title_en)
              const rawContent = localizedName(lang, page.content_uk, page.content_en) || page.content_uk
              const excerpt = rawContent.replace(/[#*•\n\r]+/g, ' ').substring(0, 130).trim() + '...'
              return (
                <Link
                  key={page.slug}
                  to="/pages/$slug"
                  params={{ slug: page.slug }}
                  className="group block rounded-2xl border border-[#2a2f36] bg-[#1b1f24] p-4 transition hover:border-[#c9a24b]/40 hover:bg-[#20242c]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-sm font-bold text-white transition group-hover:text-[#c9a24b]">
                      {title}
                    </h3>
                    <ArrowRight className="h-4 w-4 shrink-0 text-[#6a7078] transition group-hover:translate-x-1 group-hover:text-[#c9a24b]" />
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-[#aab0b8] line-clamp-2">
                    {excerpt}
                  </p>
                </Link>
              )
            })}
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
